import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Instagram, 
  Facebook, 
  Youtube,
  Heart,
  CreditCard,
  Truck,
  ShieldCheck,
  Sparkles
} from 'lucide-react';
import { siteInfo, categories } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer-section relative overflow-hidden">
      {/* Rainbow gradient top border */}
      <div className="h-1.5 bg-gradient-to-r from-[#FF6B6B] via-[#FFB800] via-[#00C853] via-[#00B4D8] to-[#9D4EDD]" />
      
      {/* Trust Badges */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#9D4EDD]/10">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Truck, title: 'Free Shipping', desc: 'On orders over $200', color: '#9D4EDD' },
              { icon: ShieldCheck, title: 'Secure Payment', desc: '100% protected', color: '#00C853' },
              { icon: CreditCard, title: 'Easy Returns', desc: '7-day policy', color: '#00B4D8' },
              { icon: Heart, title: 'Authentic Crystals', desc: 'Ethically sourced', color: '#FF6B9D' },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-4 group">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 shadow-lg"
                  style={{ 
                    background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}10 100%)`,
                    border: `2px solid ${item.color}30`
                  }}
                >
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-[#1A1A2E]">{item.title}</h4>
                  <p className="text-xs text-[#7A7A9A]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="bg-gradient-to-b from-white to-[#F8F5FF]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <Link to="/" className="flex items-center gap-3 mb-6 group">
                <div className="w-12 h-12 bg-gradient-to-br from-[#9D4EDD] via-[#FF6B9D] to-[#FFB800] rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-white font-serif text-xl font-bold">G</span>
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#1A1A2E]">Gemstone Minerals<span className="text-[#c9a96e] text-xs align-super">&reg;</span></h3>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gradient-vibrant font-bold">Crystal Healing</p>
                </div>
              </Link>
              <p className="text-sm text-[#4A4A6A] leading-relaxed mb-6">
                Embark on a transformative journey with our hand-selected crystals. 
                Each piece is chosen for its exceptional energy and quality.
              </p>
              {/* Social Links */}
              <div className="flex gap-3">
                {[
                  { icon: Instagram, color: '#FF6B9D' },
                  { icon: Facebook, color: '#00B4D8' },
                  { icon: Youtube, color: '#FF6B6B' },
                ].map((social, index) => (
                  <a
                    key={index}
                    href="#"
                    className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-md"
                    style={{ 
                      background: `linear-gradient(135deg, ${social.color}15 0%, white 100%)`,
                      border: `2px solid ${social.color}30`
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <social.icon className="w-4 h-4" style={{ color: social.color }} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-serif text-lg mb-6 text-[#1A1A2E] flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FFB800]" />
                Quick Links
              </h4>
              <ul className="space-y-3">
                {[
                  { name: 'Sacred Six Collection', path: '/collection' },
                  { name: 'Crystal Shop', path: '/shop' },
                  { name: 'Crystals of the Pulse', path: '/book' },
                  { name: 'Metaphysical Hub', path: '/hub' },
                  { name: 'Business Directory', path: '/directory' },
                ].map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className="text-[#4A4A6A] hover:text-[#9D4EDD] transition-colors text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Categories */}
            <div>
              <h4 className="font-serif text-lg mb-6 text-[#1A1A2E] flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FF6B9D]" />
                Categories
              </h4>
              <ul className="space-y-3">
                {categories.map((category) => (
                  <li key={category.id}>
                    <Link 
                      to={`/shop?category=${category.slug}`} 
                      className="text-[#4A4A6A] hover:text-[#9D4EDD] transition-colors flex items-center gap-2 text-sm"
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      {category.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-serif text-lg mb-6 text-[#1A1A2E] flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#00C853]" />
                Contact Us
              </h4>
              <ul className="space-y-4">
                <li>
                  <a 
                    href={`tel:${siteInfo.phone}`} 
                    className="flex items-center gap-3 text-[#4A4A6A] hover:text-[#FFB800] transition-colors"
                  >
                    <Phone className="w-4 h-4 text-[#FFB800]" />
                    {siteInfo.phone}
                  </a>
                </li>
                <li>
                  <a 
                    href={`mailto:${siteInfo.email}`} 
                    className="flex items-center gap-3 text-[#4A4A6A] hover:text-[#FF6B9D] transition-colors"
                  >
                    <Mail className="w-4 h-4 text-[#FF6B9D]" />
                    {siteInfo.email}
                  </a>
                </li>
                <li className="flex items-start gap-3 text-[#4A4A6A]">
                  <MapPin className="w-4 h-4 text-[#00B4D8] mt-1 flex-shrink-0" />
                  <span>Los Angeles, California<br />United States</span>
                </li>
              </ul>

              {/* Business Hours */}
              <div className="mt-6 pt-6 border-t border-[#9D4EDD]/10">
                <p className="text-xs text-[#7A7A9A] uppercase tracking-wider mb-2">Customer Support</p>
                <p className="text-sm text-[#4A4A6A]">Mon - Fri: 9AM - 6PM PST</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#F8F5FF] border-t border-[#9D4EDD]/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[#7A7A9A]">
              © {currentYear} Gemstone Minerals. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {['Privacy Policy', 'Terms of Service', 'Shipping Policy', 'Returns'].map((item) => (
                <Link
                  key={item}
                  to={`/${item.toLowerCase().replace(' ', '-')}`}
                  className="text-xs text-[#7A7A9A] hover:text-[#9D4EDD] transition-colors"
                >
                  {item}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
